import React from 'react';
import FeedingSyringeIcon from './feedingsyringe_icon';
import PipetteIcon from './pipette_icon';

interface PillShapeProps {
  color: string;
  shape: string;
  size?: number;
}

interface PillBaseProps {
  size: number;
  color: string;
  shadow: string;
  highlight: string;
}

function darkenHex(hex: string, factor = 0.78): string {
  const c = hex.replace('#', '');
  const r = Math.round(parseInt(c.slice(0, 2), 16) * factor).toString(16).padStart(2, '0');
  const g = Math.round(parseInt(c.slice(2, 4), 16) * factor).toString(16).padStart(2, '0');
  const b = Math.round(parseInt(c.slice(4, 6), 16) * factor).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

export function Capsule1PillIcon({ size, color, shadow, highlight }: PillBaseProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 58.7 62.3">
      <path fill={color} d="M40.9,1.5S11.3,25.4,2.9,40.9c-8.4,15.5,5.4,20.6,8.2,21.1s8.7-1.7,12.1-4.5,29.6-26.2,34.1-38.1c2.3-6.1.7-10.6-1.4-13.5S50.3.9,46.6.4c0,0-3.4-.7-5.7,1.1Z"/>
      <path fill={highlight} d="M46,26.3c-.4-.8-.3-1.9.4-2.6,6.2-6,4.2-14.3,4.1-14.7-.3-1.1.4-2.3,1.6-2.6s2.3.4,2.6,1.5c0,.4,2.8,11-5.3,18.8-.9.8-2.2.8-3,0,0-.1-.3-.3-.3-.5h0Z"/>
      <circle fill={highlight} cx="43.2" cy="30.4" r="2.4"/>
    </svg>
  );
}

export function Capsule2PillIcon({ size, color, shadow }: PillBaseProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 62 62">
      <path fill={color} d="M57.6,4.3h0c5.4,5.4,5.4,14.2,0,19.7L24,57.6c-5.4,5.4-14.2,5.4-19.7,0h0c-5.4-5.4-5.4-14.2,0-19.7L38,4.3c5.4-5.4,14.2-5.4,19.7,0Z"/>
      <path fill={shadow} d="M32.5,29.5c2.5,2.4,4.9,5.3,7.3,9.4h0c.2.4-.1.9-.6.7-3.4-1.5-6.7-3.8-9.6-6.8l-.2-.2c-2.5-2.4-5-5.4-7.4-9.6h0c-.2-.4.1-.9.6-.7,3.4,1.5,6.9,4,9.8,7h.1Z"/>
    </svg>
  );
}

export function Capsule3PillIcon({ size, color, shadow, highlight }: PillBaseProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 64.5 64.5">
      <path fill={shadow} d="M22.3,22.7L41,4.1c5.4-5.4,14.1-5.4,19.5,0h0c5.4,5.4,5.4,14.1,0,19.5l-18.6,18.6s-19.6-19.4-19.6-19.4Z"/>
      <path fill={color} d="M4,60.5c5.1,5.1,13.5,5,18.7,0l18-18-18.6-18.4L4.2,41.9c-5.2,5.2-5.2,13.6,0,18.7h0"/>
      <path fill={highlight} d="M41.6,36.7c-.8,0-1.6-.3-2.2-.9-1.1-1.2-1.1-3.1,0-4.2l11.8-11.3c1.2-1.1,3.1-1.1,4.2,0,1.1,1.2,1.1,3.1,0,4.2l-11.8,11.3c-.6.6-1.3.8-2.1.8h0Z"/>
      <circle fill={highlight} cx="33.1" cy="42.4" r="2.8"/>
    </svg>
  );
}

export function DiamondPillIcon({ size, color, shadow }: PillBaseProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 37.7 60.2">
      <path fill={color} d="M18.7,60c-1.4,0-2.8-.7-4.5-2.4-1.8-1.8-8.8-13-12.5-20.7-3.7-7.7.7-15.7.7-15.7,0,0,10-16.7,12.2-18.9C16.8,0,18.9.3,18.9.3h0s2.1-.2,4.3,2c2.2,2.2,12.2,18.9,12.2,18.9,0,0,4.4,8.1.7,15.7-3.7,7.7-10.7,18.9-12.5,20.7s-3.1,2.4-4.5,2.4h-.3Z"/>
    </svg>
  );
}

export function Round1PillIcon({ size, color, shadow, highlight }: PillBaseProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 59.8 59.8">
      <circle fill={color} cx="29.9" cy="29.9" r="29.7"/>
      <path fill={highlight} d="M49.1,28.6c-1.3,0-2.5-.9-2.9-2.2-3.2-11.6-14.7-14.6-15.2-14.8-1.6-.4-2.6-2-2.2-3.6s2-2.6,3.6-2.2c.6.2,15.4,4,19.6,19,.4,1.6-.5,3.3-2.1,3.7-.3,0-.5.1-.8.1Z"/>
      <circle fill={highlight} cx="50.2" cy="36" r="3.1"/>
    </svg>
  );
}

export function Round2PillIcon({ size, color, shadow }: PillBaseProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 60.5 60.5">
      <circle fill={color} cx="30.2" cy="30.2" r="30"/>
      <path fill={shadow} d="M27.4,26.9c4.9-4.9,10.6-9.7,18.9-14.6h0c.9-.5,2,.3,1.6,1.3-3,6.9-7.6,13.4-13.7,19.2l-.4.4c-4.9,4.9-10.9,10-19.2,14.9h0c-.9.5-2-.3-1.6-1.3,3-6.9,8-13.8,14.1-19.6l.3-.3Z"/>
    </svg>
  );
}

export function TrianglePillIcon({ size, color, shadow, highlight }: PillBaseProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 45.7 42.9">
      <path fill={color} d="M17.6,1.8S7.2,11.3,1.9,25.2s4,16.7,7.7,17.1,23,.4,25.8-.1,14.9-4.5,7.9-18.7C36.4,9.2,25.1,1.1,25.1,1.1c0,0-3.9-2.4-7.4.8h-.1Z"/>
    </svg>
  );
}

function FeedingSyringePillIcon({ size, color }: { size: number; color: string }) {
  return (
    <FeedingSyringeIcon
      width={size}
      height={size}
      className="med-type-svg"
      style={{ color }}
    />
  );
}

function PipettePillIcon({ size, color }: { size: number; color: string }) {
  return (
    <PipetteIcon
      width={size}
      height={size}
      className="med-type-svg"
      style={{ color }}
    />
  );
}

export function PillShape({ color, shape, size = 44 }: PillShapeProps) {
  const shadow = darkenHex(color);
  const highlight = '#ffffff';

  switch (shape) {
    case 'capsule1':
    case 'capsule':
      return <Capsule1PillIcon size={size} color={color} shadow={shadow} highlight={highlight} />;
    case 'capsule2':
    case 'oval':
      return <Capsule2PillIcon size={size} color={color} shadow={shadow} highlight={highlight} />;
    case 'capsule3':
      return <Capsule3PillIcon size={size} color={color} shadow={shadow} highlight={highlight} />;
    case 'diamond':
      return <DiamondPillIcon size={size} color={color} shadow={shadow} highlight={highlight} />;
    case 'round1':
    case 'circle':
      return <Round1PillIcon size={size} color={color} shadow={shadow} highlight={highlight} />;
    case 'round2':
      return <Round2PillIcon size={size} color={color} shadow={shadow} highlight={highlight} />;
    case 'triangle':
      return <TrianglePillIcon size={size} color={color} shadow={shadow} highlight={highlight} />;
    case 'feedingsyringe':
      return <FeedingSyringePillIcon size={size} color={color} />;
    case 'pipette':
      return <PipettePillIcon size={size} color={color} />;
    default:
      return <div className="w-10 h-10 rounded-full" style={{ backgroundColor: color }} />;
  }
}
